import { useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, Image as ImageIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { EchoflicksSessionMedia } from "../types";
import { adjacentGalleryImages } from "./galleryPreload";
import { isVideo } from "./sessionUtils";
import {
  preloadDisplayableImage,
  useDisplayableImage,
} from "./useDisplayableImage";

export function MediaGalleryDialog({
  items,
  activeIndex,
  onActiveIndexChange,
  title = "Uploaded media gallery",
}: {
  items: EchoflicksSessionMedia[];
  activeIndex: number | null;
  onActiveIndexChange: (index: number | null) => void;
  title?: string;
}) {
  const selected = activeIndex === null ? null : items[activeIndex];
  const imagesToPreload = useMemo(
    () => adjacentGalleryImages(items, activeIndex),
    [activeIndex, items],
  );
  const move = (offset: number) => {
    if (activeIndex === null || items.length < 2) return;
    onActiveIndexChange((activeIndex + offset + items.length) % items.length);
  };

  return (
    <Dialog
      open={Boolean(selected)}
      onOpenChange={(open) => {
        if (!open) onActiveIndexChange(null);
      }}
    >
      <DialogContent
        className="h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-none gap-0 overflow-hidden border-white/15 bg-black p-0 text-white sm:h-[calc(100dvh-2rem)] sm:max-w-[calc(100vw-2rem)] [&>[data-slot=dialog-close]]:z-20 [&>[data-slot=dialog-close]]:text-white"
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            move(-1);
          } else if (event.key === "ArrowRight") {
            event.preventDefault();
            move(1);
          }
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {selected && activeIndex !== null
              ? `${isGalleryVideo(selected) ? "Video" : "Photo"} ${activeIndex + 1} of ${items.length}`
              : "Uploaded photos and videos"}
          </DialogDescription>
        </DialogHeader>
        {selected && activeIndex !== null ? (
          <div className="flex h-full min-h-0 flex-col">
            <div className="pointer-events-none absolute left-4 top-4 z-10 rounded-full bg-black/65 px-3 py-1 text-sm font-medium text-white">
              <span aria-live="polite">
                {activeIndex + 1} of {items.length}
              </span>
            </div>

            <div className="relative flex min-h-0 flex-1 items-center justify-center px-14 py-12 sm:px-20">
              <GalleryMedia key={String(selected.id)} media={selected} />
              {items.length > 1 ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => move(-1)}
                    aria-label="Previous upload"
                    className="absolute left-2 top-1/2 size-11 -translate-y-1/2 rounded-full border-white/25 bg-black/65 text-white hover:bg-white/20 hover:text-white sm:left-4 sm:size-12"
                  >
                    <ChevronLeft className="size-6" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => move(1)}
                    aria-label="Next upload"
                    className="absolute right-2 top-1/2 size-11 -translate-y-1/2 rounded-full border-white/25 bg-black/65 text-white hover:bg-white/20 hover:text-white sm:right-4 sm:size-12"
                  >
                    <ChevronRight className="size-6" />
                  </Button>
                </>
              ) : null}
            </div>

            {galleryCaption(selected) ? (
              <p className="border-t border-white/10 bg-black/80 px-4 py-3 text-center text-sm text-white/80">
                {galleryCaption(selected)}
              </p>
            ) : null}
          </div>
        ) : null}
        {imagesToPreload.map((media) => (
          <GalleryImagePreloader
            key={`gallery-preload-${String(media.id)}`}
            media={media}
          />
        ))}
      </DialogContent>
    </Dialog>
  );
}

function GalleryImagePreloader({ media }: { media: EchoflicksSessionMedia }) {
  const mediaUrl = useDisplayableImage(media);

  useEffect(() => {
    if (!mediaUrl.url) return;
    void preloadDisplayableImage(mediaUrl.url).catch(() => {
      // The visible image keeps its normal retry/error path; preloading is best effort.
    });
  }, [mediaUrl.url]);

  return null;
}

function GalleryMedia({ media }: { media: EchoflicksSessionMedia }) {
  const mediaUrl = useDisplayableImage(media);
  const video = isGalleryVideo(media);
  const label =
    galleryCaption(media) ??
    media.s3_key ??
    (video ? "Uploaded video" : "Uploaded photo");

  if (!mediaUrl.url) {
    return (
      <div className="flex flex-col items-center gap-3 text-center text-sm text-white/70">
        <ImageIcon className="size-8" />
        <p>
          {mediaUrl.isLoading
            ? `Preparing this ${video ? "video" : "photo"}...`
            : (mediaUrl.error ??
              `This ${video ? "video" : "photo"} could not be displayed.`)}
        </p>
        {mediaUrl.status === "error" ? (
          <Button type="button" variant="outline" onClick={mediaUrl.refresh}>
            Try again
          </Button>
        ) : null}
      </div>
    );
  }

  return video ? (
    <video
      src={mediaUrl.url}
      aria-label={label}
      controls
      playsInline
      preload="metadata"
      className="max-h-full max-w-full object-contain"
      onError={mediaUrl.refresh}
    />
  ) : (
    <img
      src={mediaUrl.url}
      alt={label}
      className="max-h-full max-w-full object-contain"
      onError={mediaUrl.refresh}
    />
  );
}

function isGalleryVideo(media: EchoflicksSessionMedia): boolean {
  if (isVideo(media.mime_type)) return true;
  const path =
    media.storage_path ?? media.s3_key ?? media.local_path ?? media.url ?? "";
  return /\.(mp4|m4v|mov|webm)(?:$|[?#])/i.test(path);
}

// Caption = the room the director's room map assigned (FPR-033), then what
// Gemini saw. Either alone still captions; neither returns null.
function galleryCaption(media: EchoflicksSessionMedia): string | null {
  const room = media.room?.name?.trim() || null;
  const caption = media.gemini_caption?.trim();
  const match = caption?.match(/^\S+\s*[–-]\s*([\s\S]*)$/);
  const body = caption ? (match ? match[1].trim() : caption) : null;
  if (room && body) return `${room} · ${body}`;
  return room ?? body;
}
